import React, { useRef, useEffect, useState } from 'react';

import cls from '@soluto-design/styles/cls';

import { Button, Text } from '@soluto-design/react';

import { useIsMounted } from '../../../hooks/useIsMounted';

export interface HeaderAlertProps {
    children: React.ReactNode;
    onClose?: () => void;
    fixed?: boolean;
    delay?: number;
    closed?: boolean;
}

const ANIMATION_DURATION = 300;

export default function HeaderAlert({
    children,
    onClose,
    delay = 1000,
    fixed = true,
}: HeaderAlertProps) {
    const [isOpen, setIsOpen] = useState(false);
    const wrapperRef = useRef<HTMLDivElement>(null);

    const isMounted = useIsMounted();

    useEffect(() => {
        if (isMounted()) {
            setIsOpen(true);
            setTimeout(() => {
                document.body?.style.setProperty('--header-alert-delay', `0ms`);
            }, ANIMATION_DURATION);
        }
    }, [isMounted]);

    useEffect(() => {
        if (typeof window === 'undefined' || !wrapperRef?.current || !fixed) {
            return;
        }

        const setThisHeight = () => {
            document.body?.style.setProperty(
                '--header-alert-height',
                `${wrapperRef?.current?.offsetHeight}px`,
            );

            if (!isOpen) {
                document.body?.style.setProperty(
                    '--header-alert-delay',
                    `${delay}ms`,
                );
            }
        };

        setThisHeight();

        window.addEventListener('resize', setThisHeight);

        return () => {
            window.removeEventListener('resize', setThisHeight);
        };
    }, [fixed, delay, isOpen]);

    const thisClose = () => {
        if (!wrapperRef.current || typeof document === 'undefined') {
            return;
        }

        setIsOpen(false);
        wrapperRef.current.dataset.hide = 'true';
        document.body?.style.setProperty('--header-alert-height', `0px`);
        typeof onClose === 'function' && onClose();
    };

    return (
        <div
            id="banner"
            ref={wrapperRef}
            className={cls(
                'flex gap-x-sm bg-background-invert rounded-b-large pt-16 px-margin pb-20 items-center opacity-0 -translate-y-full transition-transform duration-300 ease-in-out backdrop-blur md:py-8 lg:min-h-48 xl:min-h-56',
                fixed && 'fixed top-0 left-0 right-0 z-50',
                isOpen && isMounted() && 'translate-y-0 opacity-100',
            )}
            style={{
                transitionDelay: `${delay}ms`,
            }}>
            <Text
                className={cls(
                    'flex-1 text-b400 text-text-invert [&_a]:!text-text-invert [&_a:hover]:!text-text-secondary [&_a]:text-b400 [&_a]:!no-underline',
                )}
                isHtml
                component="div">
                {children}
            </Text>
            <Button
                component="button"
                leftIcon="x"
                size="icon"
                variant="icon"
                className="!text-text-invert hover:!text-text-secondary"
                onClick={thisClose}
            />
        </div>
    );
}
